import {
  convertDateTimeToTimeString,
  isTimeValid,
} from "../../../../../../dataGrid/gridUtilities";
import appConstant from "../../../../../../../appConstants";
import { DateTimeEditorWithLabel } from "../components/DateTimeEditor";
import { withStyles } from "@material-ui/core";
import FormSectionHeader from "../common/FormSectionHeader";
import { isValid as isDateTimeValid, format, addDays } from "date-fns";
import { getFieldError, getShipperPickupWindow } from "./utils";
import { getTerminalDateByOffset } from "../../../../utils/utils";
import { memo } from "react";
import {
  PICKUP_MANAGEMENT_MODES,
  FORM_VALIDATION_MSG,
  MAX_LENGTH_CONSTRAINTS,
} from "../constants";
import { isVoidPickupMode, isCreatePickupMode } from "../utils";
import { TextFieldWithLabel } from "../components/TextField";
const styles = (theme) => ({
  label: {
    minWidth: 100,
    marginTop: 8,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
    border: `1px solid ${theme.AppBGColor}`,
    borderRadius: "4px",
    padding: 8,
    justifyContent: "space-between",
  },
  input: {
    marginRight: 8,
  },
  fieldContainer: {
    alignItems: "flex-start",
  },
  routeLabel: {
    minWidth: 60,
    marginTop: 8,
  },
  readOnlyLabel: {
    marginTop: 0,
  },
  warningText:{
    fontSize:12,
    color: theme.warningTextColorDispatch
  },
  route: {
    width: 100,
    margin: 0,
  },
});
const PickupInfo = ({
  pickupInfo,
  pickupErrorInfo,
  updateFormData,
  mode,
  classes,
  pickupData,
  selectedTerminalOffset,
  shipperInfo,
  updateSpecialInstructions,
}) => {
  const isReadOnly = isVoidPickupMode(mode);
  const handleTimeChange = (name) => (value) => {
    if (!value || isTimeValid(convertDateTimeToTimeString(value))) {
      const readyTime = name === "readyTime" ? value : pickupInfo.readyTime;
      const closeTime = name === "closeTime" ? value : pickupInfo.closeTime;
      const pickupDateTimeData = {
        ...pickupInfo,
        [name]: value,
      };
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        readyTime:
          name === "readyTime"
            ? getFieldError({ field: "readyTime", value: readyTime })
            : pickupErrorInfo.readyTime,
        closeTime:
          name === "closeTime" || pickupInfo.closeTime
            ? getFieldError({ field: "closeTime", value: closeTime, readyTime })
            : pickupErrorInfo.closeTime,
      };
      updateFormData(
        "pickupDateTimeInfo",
        pickupDateTimeData,
        pickupDateTimeErrorData
      );
    }
  };

  const resetDateTimeError = (name) => {
    if (pickupErrorInfo[name]) {
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        [name]: null,
      };
      updateFormData("pickupDateTimeInfo", pickupInfo, pickupDateTimeErrorData);
    }
  };

  const handlePickupDate = (value) => {
    if ((!value || isDateTimeValid(value)) && pickupInfo.pickupDate !== value) {
      const pickupDateTimeData = {
        ...pickupInfo,
        pickupDate: value,
      };
      const pickupDateTimeErrorData = {
        ...pickupErrorInfo,
        pickupDate: getFieldError({
          field: "pickupDate",
          value,
          mode,
          terminalOffset: selectedTerminalOffset,
        }),
      };
      updateFormData(
        "pickupDateTimeInfo",
        pickupDateTimeData,
        pickupDateTimeErrorData
      );
      isCreatePickupMode(mode) && updateSpecialInstructions({
        specialInstructions: shipperInfo.shipperSpecialInstructions,
        pickupDate: value,
        pickupWindow: shipperInfo.pickupWindow,
      });
    }
  };

  const handleRouteChange = (value) => {
    const pickupDateTimeData = {
      ...pickupInfo,
      route: value,
    };
    updateFormData("pickupDateTimeInfo", pickupDateTimeData, pickupErrorInfo);
  };

  const pickupDate = pickupInfo?.pickupDate
    ? format(pickupInfo?.pickupDate, appConstant.DATETYPES.MONTHDATEYEAR.format)
    : null;
  const terminalDate = new Date(
    getTerminalDateByOffset(new Date(), selectedTerminalOffset)
  );

  const isPickupDateReadOnly = isReadOnly || Boolean(pickupData?.driverId);

  const shipperPickupWindow = getShipperPickupWindow(
    new Date(pickupInfo.pickupDate),
    shipperInfo?.pickupWindow
  );
  return (
    <>
      <FormSectionHeader label="Pickup Info" />
      <div className={classes.container} id="pickupInfo">
        <TextFieldWithLabel
          label="Route"
          id="route"
          value={pickupInfo.route}
          className={classes.route}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          inputProps={{
            maxLength: MAX_LENGTH_CONSTRAINTS.CREATE_PICKUP_FORM.ROUTE,
          }}
          labelClass={`${classes.routeLabel} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          isReadOnly={isReadOnly}
          handleChange={handleRouteChange}
        />
        <DateTimeEditorWithLabel
          id="readyTime"
          label="Pickup Ready"
          dateType={appConstant.DATETYPES.TIME}
          labelClass={`${classes.label} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          inputClass={classes.input}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          value={convertDateTimeToTimeString(pickupInfo.readyTime)}
          onChange={handleTimeChange("readyTime")}
          disableFocusInput
          enablePickerPortal
          onKeyDown={() => resetDateTimeError("readyTime")}
          errorText={pickupErrorInfo.readyTime ?? ""}
          isReadOnly={isReadOnly}
        />
        <DateTimeEditorWithLabel
          id="closeTime"
          label="Close Time"
          dateType={appConstant.DATETYPES.TIME}
          labelClass={`${classes.label} ${
            isReadOnly ? classes.readOnlyLabel : ""
          }`}
          inputClass={classes.input}
          containerClass={isReadOnly ? "" : classes.fieldContainer}
          value={convertDateTimeToTimeString(pickupInfo.closeTime)}
          onChange={handleTimeChange("closeTime")}
          disableFocusInput
          enablePickerPortal
          errorText={pickupErrorInfo.closeTime ?? ""}
          onKeyDown={() => resetDateTimeError("closeTime")}
          isReadOnly={isReadOnly}
        />

        <DateTimeEditorWithLabel
          id="pickupDate"
          label="Pickup Date"
          labelClass={`${classes.label} ${
            isPickupDateReadOnly ? classes.readOnlyLabel : ""
          }`}
          containerClass={isPickupDateReadOnly ? "" : classes.fieldContainer}
          dateType={{
            ...appConstant.DATETYPES.MONTHDATEYEAR,
            updateFormat: "yyyy-MM-dd'T'00:00:00.000",
          }}
          minDate={terminalDate}
          maxDate={
            [
              PICKUP_MANAGEMENT_MODES.EDIT_PICKUP,
              PICKUP_MANAGEMENT_MODES.MERGE_PICKUP,
            ].includes(mode)
              ? addDays(terminalDate, 7)
              : null
          }
          value={pickupDate}
          onChange={handlePickupDate}
          disableFocusInput
          enablePickerPortal
          onKeyDown={() => resetDateTimeError("pickupDate")}
          errorText={pickupErrorInfo.pickupDate ?? ""}
          minDateMessage={FORM_VALIDATION_MSG.MIN_PICKUP_DATE}
          maxDateMessage={
            [
              PICKUP_MANAGEMENT_MODES.EDIT_PICKUP,
              PICKUP_MANAGEMENT_MODES.MERGE_PICKUP,
            ].includes(mode)
              ? FORM_VALIDATION_MSG.MAX_PICKUP_DATE
              : null
          }
          isReadOnly={isPickupDateReadOnly}
        />
      </div>
      {shipperPickupWindow.isClosed ? (
        <div className={classes.warningText}>
          {`${shipperInfo.shipperName} is closed on ${shipperPickupWindow.dayOfWeek}`}
        </div>
      ) : null}
    </>
  );
};

export default withStyles(styles)(memo(PickupInfo));
